import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { useOnboardingFormStore } from '@/lib/onboarding-form-store';
import { useAppForm } from '@/lib/use-app-form';
import type { Locale } from '@worldcoo/monolith/core';
import { Card, CardContent, CardHeader, CardTitle } from '@worldcoo/ui/components/card';
import { step1Schema } from '@/lib/onboarding-schemas';
import { useUploadTexts } from './-components/use-upload-texts';

export const Route = createFileRoute('/ngos/onboarding/')({
	component: RouteComponent,
});

function RouteComponent() {
	const { t, i18n } = useLingui();
	const countryFieldTexts = {
		search: t`Search...`,
		popular: t`Popular`,
		allCountries: t`All countries`,
		noCountryFound: t`No country found.`,
	};
	const regionFieldTexts = { search: t`Search...`, noResults: t`No results found.` };
	const provinceFieldTexts = { search: t`Search...`, noResults: t`No results found.` };

	const actionScopeOptions = [
		{ value: 'waterAndEnergy', label: t`Water & Energy` },
		{ value: 'education', label: t`Education` },
		{ value: 'humanRights', label: t`Human Rights` },
		{ value: 'health', label: t`Health` },
		{ value: 'environment', label: t`Environment` },
		{ value: 'foodSecurity', label: t`Food Security` },
		{ value: 'habitatAndPoverty', label: t`Habitat & Poverty` },
	];
	const navigate = useNavigate();
	const uploadTexts = useUploadTexts();
	const { formData, setFormData, setCurrentStep } = useOnboardingFormStore();

	const form = useAppForm({
		defaultValues: {
			name: formData.name,
			web: formData.web,
			taxId: formData.taxId,
			country: formData.country,
			countries: formData.countries,
			logo: formData.logo,
			address: formData.address,
			postalCode: formData.postalCode,
			city: formData.city,
			region: formData.region,
			province: formData.province,
			privacyUrl: formData.privacyUrl,
			actionScope: formData.actionScope,
		},
		validators: {
			onSubmit: step1Schema,
		},
		onSubmit: async ({ value }) => {
			setFormData({ ...value, logo: value.logo });
			setCurrentStep(2);
			navigate({ to: '/ngos/onboarding/contacts', search: (prev) => prev });
		},
	});

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		form.handleSubmit();
	};

	return (
		<form id="onboarding-step-form" onSubmit={handleSubmit} className="flex flex-col gap-6">
			{/* Basic Information */}
			<Card className="shadow-warm">
				<CardHeader>
					<CardTitle className="text-xl">{t`Basic Information`}</CardTitle>
				</CardHeader>
				<CardContent className="flex flex-col gap-6">
					<form.AppField
						name="name"
						children={(field) => <field.TextField label={t`Entity name`} placeholder={t`Doctors Without Borders`} />}
					/>
					<form.AppField
						name="web"
						children={(field) => <field.TextField label={t`Website`} placeholder="https://www.example.org" />}
					/>
					<form.AppField
						name="taxId"
						children={(field) => <field.TextField label={t`Tax ID (CIF/NIF)`} placeholder={t`G12345678`} />}
					/>
					<form.AppField name="address" children={(field) => <field.TextField label={t`Address`} />} />
					<div className="flex gap-4">
						<form.AppField name="city" children={(field) => <field.TextField label={t`City`} className="w-full" />} />
						<form.AppField
							name="postalCode"
							children={(field) => <field.TextField label={t`Postal Code`} className="w-full" />}
						/>
					</div>
					<form.AppField
						name="country"
						listeners={{
							onChange: ({ value }) => {
								form.setFieldValue('region', '', { dontValidate: true, dontUpdateMeta: true });
								form.setFieldValue('province', '', { dontValidate: true, dontUpdateMeta: true });
								if (value) {
									const current = form.getFieldValue('countries');
									if (!current.includes(value)) {
										form.setFieldValue('countries', [...current, value]);
									}
								}
							},
						}}
						children={(field) => (
							<field.CountryField label={t`Country`} locale={i18n.locale as Locale} texts={countryFieldTexts} />
						)}
					/>
					<form.Subscribe selector={(state) => ({ country: state.values.country, region: state.values.region })}>
						{({ country, region }) => (
							<>
								<form.AppField
									name="region"
									validators={{ onChangeListenTo: ['country'] }}
									listeners={{
										onChange: () => form.setFieldValue('province', '', { dontValidate: true, dontUpdateMeta: true }),
									}}
									children={(field) => (
										<field.RegionField label={t`Region`} country={country} texts={regionFieldTexts} />
									)}
								/>
								<form.AppField
									name="province"
									validators={{ onChangeListenTo: ['country', 'region'] }}
									children={(field) => (
										<field.ProvinceField
											label={t`Province`}
											region={region}
											country={country}
											texts={provinceFieldTexts}
										/>
									)}
								/>
							</>
						)}
					</form.Subscribe>
				</CardContent>
			</Card>

			{/* Logo */}
			<Card className="shadow-warm">
				<CardHeader>
					<CardTitle className="text-xl">{t`Logo`}</CardTitle>
				</CardHeader>
				<CardContent>
					<form.AppField
						name="logo"
						children={(field) => (
							<field.ImageUploadField
								{...uploadTexts.imageUpload()}
								currentUrl={undefined}
								buttonLabel={t`Upload Logo`}
								contain
								size="compact"
							/>
						)}
					/>
				</CardContent>
			</Card>

			{/* Other Information */}
			<Card className="shadow-warm">
				<CardHeader>
					<CardTitle className="text-xl">{t`Additional information`}</CardTitle>
				</CardHeader>
				<CardContent className="flex flex-col gap-6">
					<form.AppField
						name="privacyUrl"
						children={(field) => <field.TextField label={t`Privacy Policy URL`} placeholder="https://..." />}
					/>
					<form.AppField
						name="actionScope"
						children={(field) => (
							<field.MultiComboboxTextField
								label={t`Action Scope`}
								search={t`Search…`}
								empty={t`No results found.`}
								options={actionScopeOptions}
							/>
						)}
					/>
					<form.AppField
						name="countries"
						children={(field) => (
							<field.MultiCountryField
								label={t`Countries where it operates`}
								locale={i18n.locale as Locale}
								texts={countryFieldTexts}
							/>
						)}
					/>
				</CardContent>
			</Card>
		</form>
	);
}
